import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box, Typography, Chip, Divider, Stack, Alert, Button, IconButton,
  Skeleton, Snackbar, CircularProgress, Tooltip,
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { formatPaise, FEATURE_META } from '../api/billingApi';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, GradientButton, fadeUp,
} from '../components/NextGenPortalUI';

/**
 * NextGenFeaturePlans — /nextgen-builder/plans
 *
 * The feature-credit storefront. Every feature ships a free allowance; this
 * page states that allowance in the backend's own numbers, shows what is left
 * of it right now, and sells top-ups only for what the builder actually uses.
 * Nothing here is hardcoded: quotas, unit counts, prices, tier names and the
 * tax rate all come from GET /billing/catalog.
 */

const FEATURE_ICONS = {
  AI_TUTOR: <PsychologyRoundedIcon />,
  MOCK_INTERVIEW: <RecordVoiceOverRoundedIcon />,
  ELITE_MASTERY: <AutoStoriesRoundedIcon />,
};

const ORDER = ['AI_TUTOR', 'MOCK_INTERVIEW', 'ELITE_MASTERY'];

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Build the free-allowance sentence purely from the catalog's own fields. */
export const freeRuleText = (feature) => {
  if (!feature) return '';
  const access = feature.freeAccess || null;
  if (access && access.mode === 'PERCENT') {
    const units = feature.unitLabelPlural || 'units';
    const scope = feature.quotaScope === 'TECHNOLOGY' ? ' of every technology' : '';
    const min = access.minUnits ? ` (at least ${access.minUnits} ${access.minUnits === 1 ? (feature.unitLabel || 'unit') : units})` : '';
    return `First ${access.percent}% of the ${units}${scope} free${min}`;
  }
  if (feature.freeQuota == null) return '';
  const units = feature.freeQuota === 1 ? (feature.unitLabel || 'unit') : (feature.unitLabelPlural || 'units');
  if (feature.quotaPeriod === 'NONE') {
    return feature.quotaScope === 'TECHNOLOGY'
      ? `First ${feature.freeQuota} ${units} of every technology free`
      : `First ${feature.freeQuota} ${units} free`;
  }
  const period = feature.quotaPeriod === 'MONTH' ? 'every month'
    : feature.quotaPeriod === 'WEEK' ? 'every week'
      : feature.quotaPeriod === 'DAY' ? 'every day' : '';
  const scope = feature.quotaScope === 'SUB_FEATURE' ? ' per tool'
    : feature.quotaScope === 'TECHNOLOGY' ? ' per technology' : '';
  return `${feature.freeQuota} free ${units}${scope} ${period}`.trim();
};

/** Secondary line: admin-authored copy when present, otherwise derived from the API's own fields. */
export const freeRuleDetail = (feature) => {
  if (!feature) return '';
  if (feature.display && feature.display.freeTrialDescription) return feature.display.freeTrialDescription;
  if (feature.quotaScope === 'SUB_FEATURE' && Array.isArray(feature.subFeatures) && feature.subFeatures.length) {
    return `Applies separately to each of the ${feature.subFeatures.length} tools. Resets on the 1st of every month.`;
  }
  if (feature.quotaPeriod === 'MONTH') return 'Resets on the 1st of every month.';
  if (feature.quotaPeriod === 'NONE') return 'Free pages stay free.';
  return '';
};

/** What a purchase gives, from the product's own access type and validity. */
export const productRuleText = (product, feature) => {
  if (!product) return '';
  const units = feature && feature.unitLabelPlural ? feature.unitLabelPlural : 'units';
  if (product.accessType === 'FULL_ACCESS') {
    const scope = product.purchaseScope === 'TECHNOLOGY' || product.requiresTechnology ? 'one technology' : 'the feature';
    const validity = product.validity && product.validity.label ? ` for ${product.validity.label}` : '';
    return `Every ${feature && feature.unitLabel ? feature.unitLabel : 'unit'} of ${scope}${validity} — including ${units} published during your access`;
  }
  return `${product.unitsPerPurchase} ${units} per purchase${product.requiresTechnology ? ' · one technology per purchase' : ''}`;
};

const BalanceRow = ({ label, value, tone }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
      px: 1.25, py: 0.85, borderRadius: TOKENS.radius.control,
      background: TOKENS.bg.page, border: `1px solid ${TOKENS.borderSoft}`,
    }}
  >
    <Typography variant="body2" sx={{ color: TOKENS.text.caption, fontWeight: 500, fontSize: '0.8rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.82rem', color: tone === 'out' ? '#B91C1C' : tone === 'warn' ? '#B45309' : TOKENS.text.primary, flexShrink: 0 }}>
      {value}
    </Typography>
  </Box>
);

const QuantityStepper = ({ value, min = 1, max = 10, disabled, onChange }) => (
  <Box
    sx={{
      display: 'inline-flex', alignItems: 'center',
      border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius.control,
      background: TOKENS.bg.card, overflow: 'hidden',
    }}
  >
    <IconButton
      size="small"
      aria-label="Decrease quantity"
      disabled={disabled || value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      sx={{ borderRadius: 0, color: TOKENS.text.muted }}
    >
      <RemoveRoundedIcon sx={{ fontSize: 16 }} />
    </IconButton>
    <Typography sx={{ px: 1.5, minWidth: 34, textAlign: 'center', fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.9rem' }}>
      {value}
    </Typography>
    <IconButton
      size="small"
      aria-label="Increase quantity"
      disabled={disabled || value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
      sx={{ borderRadius: 0, color: TOKENS.text.muted }}
    >
      <AddRoundedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

const FeatureCard = ({ feature, catalog, initialTechnology, onAdded, onAuthRequired }) => {
  const meta = FEATURE_META[feature.code] || { label: feature.name, units: feature.unitLabelPlural, color: TOKENS.brand.primary };
  const color = meta.color || TOKENS.brand.primary;
  const product = Array.isArray(feature.products) && feature.products.length ? feature.products[0] : null;
  const requiresTechnology = !!(product && product.requiresTechnology);
  const maxQty = product && product.maxQuantityPerOrder ? product.maxQuantityPerOrder : 1;

  const { addToCart } = useNexusBilling();
  const [technology, setTechnology] = useState(initialTechnology || '');
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialTechnology) setTechnology(initialTechnology);
  }, [initialTechnology]);

  const quote = useMemo(() => {
    if (!product) return null;
    if (requiresTechnology) {
      const map = product.quotesByTechnology || {};
      return technology ? (map[technology] || null) : (product.quote || null);
    }
    return product.quote || null;
  }, [product, requiresTechnology, technology]);

  const priced = !!(quote && !quote.unavailable && quote.unitPricePaise != null);
  const usage = feature.usage || null;
  const legacyTill = usage && usage.legacyAccess ? usage.legacyAccess.validTill : null;

  /* ── Current balance rows, straight from the summary payload ── */
  const balanceRows = useMemo(() => {
    if (!usage) return [];
    const rows = [];
    if (usage.monthlyFree && Array.isArray(usage.monthlyFree.buckets)) {
      usage.monthlyFree.buckets.forEach((b) => {
        rows.push({
          key: b.key || b.subFeature || b.name,
          label: b.name || b.subFeature || 'Free allowance',
          value: `${b.remaining} / ${b.total}`,
          tone: b.remaining === 0 ? 'out' : b.remaining <= 2 ? 'warn' : 'ok',
        });
      });
    }
    if (usage.premium && Array.isArray(usage.premium.byTechnology)) {
      usage.premium.byTechnology.forEach((t) => {
        const tech = (catalog.technologies || []).find((x) => x.slug === t.technology);
        rows.push({
          key: `premium-${t.technology}`,
          label: tech ? (tech.title || t.technology) : t.technology,
          value: t.active ? `Full access until ${formatDate(t.expiresAt)}` : `Access ended ${formatDate(t.expiresAt)}`,
          tone: t.active ? (t.daysRemaining != null && t.daysRemaining <= 14 ? 'warn' : 'ok') : 'out',
        });
      });
    }
    if (usage.paid && Array.isArray(usage.paid.byTechnology)) {
      usage.paid.byTechnology.filter((t) => t.total > 0).forEach((t) => {
        const tech = (catalog.technologies || []).find((x) => x.slug === t.technology);
        rows.push({
          key: `paid-${t.technology}`,
          label: tech ? (tech.title || t.technology) : t.technology,
          value: `${t.remaining} / ${t.total} paid ${meta.units || 'units'}`,
          tone: t.remaining === 0 ? 'out' : 'ok',
        });
      });
    }
    return rows;
  }, [usage, meta.units, catalog]);

  const paidLine = useMemo(() => {
    if (!usage || !usage.paid) return null;
    if (usage.paid.byTechnology) return null;
    return `${usage.paid.remaining || 0} paid ${meta.units || feature.unitLabelPlural || 'units'} remaining`;
  }, [usage, meta.units, feature.unitLabelPlural]);

  const canAdd = !!product && priced && (!requiresTechnology || !!technology) && !busy;

  const handleAdd = async () => {
    if (!product) return;
    setBusy(true);
    setError('');
    try {
      await addToCart({
        productCode: product.code,
        technologySlug: requiresTechnology ? technology : null,
        quantity,
      });
      onAdded(`${product.name} added to your cart`);
    } catch (e) {
      if (e && e.status === 401) { onAuthRequired(); return; }
      setError((e && e.message) || 'Could not add this to your cart');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: { xs: 2.5, md: 3 } }}
      component={motion.div}
      {...fadeUp}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.75 }}>
        <Box
          sx={{
            width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
            background: `${color}14`, border: `1px solid ${color}28`, color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& svg': { fontSize: 22 },
          }}
        >
          {FEATURE_ICONS[feature.code] || <BoltRoundedIcon />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '1.02rem', letterSpacing: '-0.01em' }}>
            {feature.name}
          </Typography>
          {feature.description && (
            <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, fontSize: '0.8rem', mt: 0.25 }}>
              {feature.description}
            </Typography>
          )}
        </Box>
      </Box>

      {legacyTill && (
        <Chip
          size="small"
          icon={<VerifiedUserRoundedIcon sx={{ fontSize: '14px !important' }} />}
          label={`Included in your current plan until ${formatDate(legacyTill)}`}
          sx={{
            alignSelf: 'flex-start', mb: 1.75, height: 24, fontWeight: 700, fontSize: '0.7rem',
            bgcolor: 'rgba(6,182,212,0.12)', color: '#0E7490', border: '1px solid rgba(6,182,212,0.26)',
            '& .MuiChip-icon': { color: '#0E7490' },
          }}
        />
      )}

      {/* Free allowance rule */}
      <Box
        sx={{
          p: 1.5, mb: 2, borderRadius: TOKENS.radius.control,
          background: 'linear-gradient(135deg, rgba(108,59,255,0.06), rgba(139,92,246,0.03))',
          border: '1px solid rgba(108,59,255,0.16)',
        }}
      >
        <Typography sx={{ fontWeight: 800, color: TOKENS.brand.primaryDark, fontSize: '0.85rem' }}>
          {freeRuleText(feature)}
        </Typography>
        {freeRuleDetail(feature) && (
          <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mt: 0.35, fontWeight: 500 }}>
            {freeRuleDetail(feature)}
          </Typography>
        )}
      </Box>

      {/* Current balance */}
      <Typography variant="caption" sx={{ fontWeight: 800, color: TOKENS.text.muted, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.66rem' }}>
        Your balance
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1, mb: 2 }}>
        {balanceRows.length ? balanceRows.map((r) => (
          <BalanceRow key={r.key} label={r.label} value={r.value} tone={r.tone} />
        )) : (
          <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, fontSize: '0.8rem' }}>
            {feature.code === 'ELITE_MASTERY' ? 'No premium access yet' : 'Balance will appear once your account is loaded'}
          </Typography>
        )}
        {paidLine && (
          <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600, pl: 0.25 }}>
            {paidLine}
          </Typography>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Paid product */}
      {product ? (
        <Box sx={{ mt: 'auto' }}>
          <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.92rem' }}>
            {product.name}
          </Typography>
          <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, fontSize: '0.8rem', mb: 1.5 }}>
            {(feature.display && feature.display.premiumDescription) || productRuleText(product, feature)}
          </Typography>

          {/* Technology selector (Elite Mastery) */}
          {requiresTechnology && (
            <Box sx={{ mb: 1.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: TOKENS.text.muted, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.66rem', display: 'block', mb: 0.85 }}>
                Choose a technology
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(catalog.technologies || []).map((t) => {
                  const q = (product.quotesByTechnology || {})[t.slug];
                  const blocked = !!(q && q.unavailable);
                  const selected = technology === t.slug;
                  const activePremium = usage && usage.premium && Array.isArray(usage.premium.byTechnology)
                    ? usage.premium.byTechnology.find((x) => x.technology === t.slug && x.active) : null;
                  const pagesHint = t.totalPages != null && t.freePages != null ? `${t.freePages} of ${t.totalPages} ${meta.units || 'pages'} free` : null;
                  const tip = blocked && q.reason ? q.reason : activePremium ? `Full access until ${formatDate(activePremium.expiresAt)}` : pagesHint || '';
                  return (
                    <Tooltip key={t.slug} title={tip}>
                      <span>
                        <Chip
                          label={pagesHint ? `${t.title || t.slug} · ${t.freePages}/${t.totalPages}` : (t.title || t.slug)}
                          size="small"
                          onClick={() => setTechnology(t.slug)}
                          disabled={blocked}
                          sx={{
                            fontWeight: 700, fontSize: '0.72rem', height: 26,
                            cursor: 'pointer',
                            bgcolor: selected ? 'rgba(108,59,255,0.12)' : TOKENS.bg.page,
                            color: selected ? TOKENS.brand.primary : TOKENS.text.caption,
                            border: `1px solid ${selected ? 'rgba(108,59,255,0.35)' : TOKENS.border}`,
                            '&:hover': { bgcolor: selected ? 'rgba(108,59,255,0.16)' : '#F1F5F9' },
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Price */}
          {priced ? (
            <Box sx={{ mb: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 900, color: TOKENS.text.primary, fontSize: '1.6rem', letterSpacing: '-0.02em' }}>
                  {formatPaise(quote.unitPricePaise)}
                </Typography>
                <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                  + {catalog.taxRate ? catalog.taxRate.percent : ''}% {catalog.taxRate ? catalog.taxRate.code : 'tax'}
                </Typography>
              </Box>
              {quote.tier && quote.tier.name && (
                <Chip
                  size="small"
                  label={`${quote.tier.name} pricing`}
                  sx={{
                    mt: 0.75, height: 22, fontWeight: 700, fontSize: '0.68rem',
                    bgcolor: 'rgba(245,158,11,0.12)', color: '#B45309', border: '1px solid rgba(245,158,11,0.26)',
                  }}
                />
              )}
            </Box>
          ) : quote && quote.unavailable ? (
            <Alert severity="warning" sx={{ mb: 1.75, borderRadius: TOKENS.radius.control }}>
              {quote.reason || 'This product is not available right now.'}
            </Alert>
          ) : requiresTechnology && !technology ? (
            <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 600, mb: 1.75, fontSize: '0.82rem' }}>
              Select a technology to see its price.
            </Typography>
          ) : null}

          {error && (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: TOKENS.radius.control }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            {maxQty > 1 && <QuantityStepper value={quantity} min={1} max={maxQty} disabled={busy} onChange={setQuantity} />}
            <GradientButton
              onClick={handleAdd}
              disabled={!canAdd}
              endIcon={null}
              startIcon={busy ? <CircularProgress size={15} sx={{ color: '#FFFFFF' }} /> : <ShoppingCartRoundedIcon sx={{ fontSize: '17px !important' }} />}
              sx={{ flex: 1, minWidth: 150, textTransform: 'none', '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } }}
            >
              {(feature.display && feature.display.ctaLabel) || 'Add to cart'}
            </GradientButton>
          </Box>
          {priced && quantity > 1 && product.accessType !== 'FULL_ACCESS' && (
            <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mt: 1, fontWeight: 600 }}>
              {quantity} × {product.unitsPerPurchase} = {quantity * product.unitsPerPurchase} {meta.units || feature.unitLabelPlural}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>
          No top-up product is available for this feature yet.
        </Typography>
      )}
    </SectionCard>
  );
};

const NextGenFeaturePlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { catalog, loading, error, refreshCatalog, refreshSummary } = useNexusBilling();
  const [snack, setSnack] = useState({ open: false, message: '' });
  const [retrying, setRetrying] = useState(false);

  const focusFeature = searchParams.get('feature');
  const focusTechnology = searchParams.get('technology');

  const features = useMemo(() => {
    if (!catalog || !Array.isArray(catalog.features)) return [];
    return [...catalog.features].sort((a, b) => {
      const ia = ORDER.indexOf(a.code);
      const ib = ORDER.indexOf(b.code);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [catalog]);

  useEffect(() => {
    if (!focusFeature || !catalog) return;
    const el = document.getElementById(`feature-card-${focusFeature}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusFeature, catalog]);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try { await refreshCatalog(); } finally { setRetrying(false); }
  }, [refreshCatalog]);

  const handleAdded = useCallback((message) => {
    setSnack({ open: true, message });
    refreshSummary();
  }, [refreshSummary]);

  const handleAuthRequired = useCallback(() => {
    navigate('/nextgen-builder/login', { state: { from: '/nextgen-builder/plans' } });
  }, [navigate]);

  const showSkeleton = !catalog && (loading || !error);

  return (
    <PageContainer maxWidth={1240}>
      <PageHeader
        icon={<BoltRoundedIcon />}
        eyebrow="TSG Nexus"
        title="Feature credits"
        description="Every feature comes with a free allowance. Buy more only for the features you actually use — nothing is bundled, nothing renews automatically."
        action={
          <Button
            startIcon={<ShoppingCartRoundedIcon />}
            onClick={() => navigate('/nextgen-builder/cart')}
            sx={{
              borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
              color: TOKENS.brand.primary, border: `1px solid ${TOKENS.border}`, px: 2,
              '&:hover': { bgcolor: 'rgba(108,59,255,0.06)', borderColor: 'rgba(108,59,255,0.35)' },
            }}
          >
            View cart
          </Button>
        }
      />

      {!catalog && error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: TOKENS.radius.control }}
          action={
            <Button
              size="small"
              onClick={handleRetry}
              disabled={retrying}
              startIcon={retrying ? <CircularProgress size={14} /> : <RefreshRoundedIcon sx={{ fontSize: '16px !important' }} />}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Retry
            </Button>
          }
        >
          {error.message || 'We could not load the feature catalog.'}
        </Alert>
      )}

      {showSkeleton ? (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={520} sx={{ borderRadius: TOKENS.radius.card }} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, alignItems: 'stretch' }}>
          {features.map((f) => (
            <Box
              key={f.code}
              id={`feature-card-${f.code}`}
              sx={{
                display: 'flex',
                borderRadius: TOKENS.radius.card,
                ...(focusFeature === f.code ? { outline: '2px solid rgba(108,59,255,0.35)', outlineOffset: 3 } : {}),
              }}
            >
              <FeatureCard
                feature={f}
                catalog={catalog}
                initialTechnology={focusFeature === f.code ? focusTechnology : null}
                onAdded={handleAdded}
                onAuthRequired={handleAuthRequired}
              />
            </Box>
          ))}
        </Box>
      )}

      {catalog && (
        <Typography variant="caption" sx={{ display: 'block', mt: 3, color: TOKENS.text.muted, fontWeight: 500 }}>
          Prices are shown in {catalog.currency} and exclude {catalog.taxRate ? `${catalog.taxRate.percent}% ${catalog.taxRate.code}` : 'tax'}, which is added at checkout.
          Purchased credits do not expire at the end of the month; time-boxed access ends on the date shown in your balance.
        </Typography>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: TOKENS.radius.control, fontWeight: 600 }}
          action={
            <Button
              size="small"
              onClick={() => navigate('/nextgen-builder/cart')}
              sx={{ fontWeight: 800, textTransform: 'none', color: TOKENS.brand.primary }}
            >
              View cart
            </Button>
          }
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default NextGenFeaturePlans;
